import React, { useEffect, useRef } from "react";

import { Sheet, SheetContent } from "../../../shadcn/components/ui/sheet";
import { cn } from "../../../shadcn/lib/utils";
import { useReturnFocus } from "../../Modal/dialogHelpers";

export const TRANSACTIONAL_DRAWER_WIDTHS = {
  compact: 400,
  wide: 600,
};

// Side panel built on the shadcn Sheet.
// - `size` ("compact" | "wide") makes a transactional drawer: fixed desktop
//   width, and it scrolls inside DrawerBody instead of as a whole.
// - `width` sets a desktop width (px) for a drawer that scrolls as a whole.
// Other props (aria-*, className) go to the panel.
const Drawer = React.forwardRef(function Drawer(
  {
    side = "right",
    open = false,
    onClose,
    onEnter,
    onExited,
    size,
    width,
    closeLabel,
    closeDisabled,
    className,
    children,
    ...props
  },
  ref
) {
  const scrollsInside = Boolean(size);
  const desktopWidth = size
    ? TRANSACTIONAL_DRAWER_WIDTHS[size] || TRANSACTIONAL_DRAWER_WIDTHS.compact
    : width;
  const finalFocus = useReturnFocus(open);
  const onEnterRef = useRef(onEnter);
  onEnterRef.current = onEnter;

  useEffect(() => {
    if (open) onEnterRef.current?.();
  }, [open]);

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen, eventDetails) => {
        if (nextOpen) return;
        onClose?.(eventDetails?.event, eventDetails?.reason);
      }}
      onOpenChangeComplete={(isOpen) => {
        if (isOpen) return;
        // Pages often unmount the drawer in onExited; return focus first so
        // it does not fall to <body>.
        const target = finalFocus();
        if (
          target instanceof HTMLElement &&
          !target.contains(document.activeElement)
        ) {
          target.focus();
        }
        onExited?.();
      }}
    >
      <SheetContent
        ref={ref}
        side={side}
        showCloseButton={Boolean(closeLabel)}
        closeLabel={closeLabel}
        closeDisabled={closeDisabled}
        finalFocus={finalFocus}
        {...props}
        style={
          desktopWidth ? { "--drawer-width": `${desktopWidth}px` } : undefined
        }
        className={cn(
          "max-w-[100vw] gap-0 bg-background p-0 motion-reduce:transition-none",
          // Drawers never scroll sideways.
          scrollsInside ? "overflow-hidden" : "overflow-x-hidden overflow-y-auto",
          "data-[side=left]:w-full data-[side=right]:w-full",
          desktopWidth
            ? "data-[side=left]:sm:w-(--drawer-width) data-[side=left]:sm:max-w-none data-[side=right]:sm:w-(--drawer-width) data-[side=right]:sm:max-w-none"
            : "data-[side=left]:sm:w-auto data-[side=right]:sm:w-auto",
          className
        )}
      >
        {children}
      </SheetContent>
    </Sheet>
  );
});

export default Drawer;
